import { badgeColor, formatDateShort, normalizeBP, vitalStatusLabel, vitalStatusColor } from './wellnessDisplayUtils';
import WellnessSummaryView from './WellnessSummaryView';
import { formatDate } from './dashboardDisplayUtils';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { supabase } from '../../../supabaseClient';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VitalGraphView from './VitalGraphView';






const WellnessOverviewSection = ({
  mode = 'MindVault',
  userId,
  currentOrgId,
  record,
  toNumOrNull, getHrStatus, getSpo2Status, getBpStatus, getHrvStatus, getSiStatus, getRrStatus, scoreLabel, clampScore
}) => {
    const [loading, setLoading] = useState(false);
    const [range, setRange] = useState('week');
    const [scans, setScans] = useState([]);

    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [appliedFrom, setAppliedFrom] = useState('');
    const [appliedTo, setAppliedTo] = useState('');

    useEffect(() => {
        const fetchScans = async () => {
            const isCorporate = mode === 'Corporates';

            if (!isCorporate && !userId) {
                setScans([]);
                return;
            }
            if (isCorporate && (!currentOrgId || !record?.id)) {
                setScans([]);
                return;
            }

            setLoading(true);
            try {
                let query = supabase
                    .from('scan_history')
                    .select('*')
                    .order('created_at', { ascending: false });

                if (isCorporate) {
                    query = query.eq('team_id', currentOrgId).is('user_id', null);

                    if (record?.session_id) {
                    query = query.eq('session_id', record.session_id);
                    } else {
                    const [source, rawId] = String(record.id).split(':');
                    if (source === 'scan' && rawId) query = query.eq('id', rawId);
                    else query = query.limit(0);
                    }
                } else {
                    query = query.eq('user_id', userId);
                }

                if (range === 'week') {
                    const from = new Date();
                    from.setDate(from.getDate() - 7);
                    from.setHours(0, 0, 0, 0);
                    query = query.gte('created_at', from.toISOString());
                } else if (range === 'month') {
                    const from = new Date();
                    from.setDate(from.getDate() - 30);
                    from.setHours(0, 0, 0, 0);
                    query = query.gte('created_at', from.toISOString());
                } else if (range === 'last') {
                    query = query.limit(1);
                } else if (range === 'custom') {
                    if (appliedFrom) query = query.gte('created_at', new Date(appliedFrom).toISOString());
                    if (appliedTo) {
                    const to = new Date(appliedTo);
                    to.setHours(23, 59, 59, 999);
                    query = query.lte('created_at', to.toISOString());
                    }
                }

                const { data, error } = await query;
                if (error) {
                    console.error('Error fetching scan_history:', error);
                    setScans([]);
                    return;
                }

                const processedData = (data || []).map((scan) => ({
                    ...scan,
                    hr_variability: Math.round(scan.hr_variability),
                }));
                setScans(processedData);
            } catch (err) {
                console.error('Error loading scan_history:', err);
                setScans([]);
            } finally {
                setLoading(false);
            }
        };
        fetchScans();
    }, [mode, userId, currentOrgId, record?.id, record?.session_id, range, appliedFrom, appliedTo]);

    const latest = useMemo(() => scans[0] || null, [scans]);

    const vitalGraphData = useMemo(() => {
        if (!scans?.length) return [];
        return scans
            .map((s) => ({
            bloodPressure: {
                sbp: Number(s.sbp) || 0,
                dbp: Number(s.dbp) || 0,
            },
            heartRate: Number(s.heart_rate) || 0,
            hrvSdnn: Number(s.hr_variability) || 0,
            oxygenSaturation: Number(s.oxygen_saturation) || 0,
            respirationRate: Number(s.respiration_rate) || 0,
            scanDatetime: new Date(s.created_at),
            scanTime: new Date(s.created_at).toLocaleTimeString(),
            stressIndex: Number(s.stress_index) || 0,
            }))
            .reverse(); // ensure ascending by time
    }, [scans]);

const vitalDateRange = useMemo(() => {
  if (range === 'custom' && appliedFrom && appliedTo) {
    return [new Date(appliedFrom), new Date(appliedTo)];
  }

  if (range === 'last') {
    const last = scans?.[0]?.created_at ? new Date(scans[0].created_at) : null;
    return last ? [last, last] : [null, null];
  }

  if (range === 'week') {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    return [start, end];
  }

  if (range === 'month') {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return [start, end];
  }

  return [null, null];
}, [range, appliedFrom, appliedTo, scans]);

    const vitalCards = useMemo(() => {
        if (!latest) return [];

        const hr = toNumOrNull(latest.heart_rate);
        const spo2 = toNumOrNull(latest.oxygen_saturation);
        const rr = toNumOrNull(latest.respiration_rate);
        const hrv = toNumOrNull(latest.hr_variability);
        const sbp = toNumOrNull(latest.sbp);
        const dbp = toNumOrNull(latest.dbp);
        const si = latest.stress_index ?? null;

        return [
            {
                label: 'Heart Rate',
                value: latest.heart_rate ?? '--',
                unit: 'bpm',
                status: getHrStatus(hr),
                icon: '❤️' 
            },
            {
                label: 'Blood Pressure',
                value: normalizeBP(latest),
                unit: 'mmHg',
                status: getBpStatus(sbp, dbp),
                icon: '🩺'
            },
            {
                label: 'Oxygen Saturation',
                value: latest.oxygen_saturation ?? '--',
                unit: '%',
                status: getSpo2Status(spo2),
                icon: '💧' 
            },
            {
                label: 'Respiratory Rate',
                value: latest.respiration_rate ?? '--',
                unit: 'rpm',
                status: getRrStatus(rr),
                icon: '🫁'
            },
            {
                label: 'Stress Index',
                value: latest.stress_index ?? '--',
                unit: '',
                status: getSiStatus(si),
                icon: '😌' 
            },
            {
                label: 'HRV',
                value: latest.hr_variability ?? '--',
                unit: 'ms',
                status: getHrvStatus(hrv),
                icon: '💓' 
            },
        ];
    }, [latest]);

    return (
        <WellnessSummaryView badgeColor={badgeColor} clampScore={clampScore} customFrom={customFrom} customTo={customTo} formatDate={formatDate} latest={latest} loading={loading} range={range} scans={scans} scoreLabel={scoreLabel} setAppliedFrom={setAppliedFrom} setAppliedTo={setAppliedTo} setCustomFrom={setCustomFrom} setCustomTo={setCustomTo} setRange={setRange} vitalCards={vitalCards} vitalDateRange={vitalDateRange} vitalGraphData={vitalGraphData} vitalStatusColor={vitalStatusColor} vitalStatusLabel={vitalStatusLabel} />
    );
};

export default WellnessOverviewSection;
